import { FormEvent, useState } from 'react';
import { Mail, MapPin, User } from 'lucide-react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { useNavigate } from 'react-router-dom';
import { submitWaitlist } from '../services/waitlistService';
import type { WaitlistSource } from '../types/waitlist';
import { cn } from '../utils/cn';
import { Button } from './Button';

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type CountryOption = {
  code: string;
  label: string;
  value: string;
};

type OptInSectionProps = {
  className?: string;
  id?: string;
  source?: WaitlistSource;
};

const europeanCountries = [
  { code: 'AL', name: 'Albania' },
  { code: 'AD', name: 'Andorra' },
  { code: 'AM', name: 'Armenia' },
  { code: 'AT', name: 'Austria' },
  { code: 'AZ', name: 'Azerbaijan' },
  { code: 'BY', name: 'Belarus' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BA', name: 'Bosnia and Herzegovina' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'GE', name: 'Georgia' },
  { code: 'DE', name: 'Germany' },
  { code: 'GR', name: 'Greece' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IS', name: 'Iceland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'XK', name: 'Kosovo' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'MD', name: 'Moldova' },
  { code: 'MC', name: 'Monaco' },
  { code: 'ME', name: 'Montenegro' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'MK', name: 'North Macedonia' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'RU', name: 'Russia' },
  { code: 'SM', name: 'San Marino' },
  { code: 'RS', name: 'Serbia' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'TR', name: 'Turkey' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'VA', name: 'Vatican City' },
] as const;

const europeanCountryOptions: CountryOption[] = europeanCountries.map((country) => ({
  code: country.code,
  label: country.name,
  value: country.name,
}));

function countryCodeToFlagEmoji(countryCode: string) {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function formatCountryOption(option: CountryOption) {
  return (
    <div className="flex items-center gap-2">
      <span aria-hidden="true" className="text-base">
        {countryCodeToFlagEmoji(option.code)}
      </span>
      <span>{option.label}</span>
    </div>
  );
}

const countrySelectStyles: StylesConfig<CountryOption, false> = {
  clearIndicator: (base) => ({
    ...base,
    color: '#64748b',
    cursor: 'pointer',
    padding: 0,
  }),
  container: (base) => ({
    ...base,
    width: '100%',
  }),
  control: (base) => ({
    ...base,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    minHeight: 0,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#64748b',
    cursor: 'pointer',
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  input: (base) => ({
    ...base,
    color: '#000000',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
    padding: 0,
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 14,
    boxShadow: '0 16px 32px -20px rgba(15,23,42,0.5)',
    overflow: 'hidden',
    zIndex: 50,
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 260,
    padding: 6,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f1f5f9' : '#ffffff',
    borderRadius: 10,
    color: '#111827',
    cursor: 'pointer',
    fontWeight: 600,
    padding: '10px 12px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#94a3b8',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    color: '#000000',
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: 0,
  }),
};

export function OptInSection({ className, id, source = 'final_cta' }: OptInSectionProps) {
  const navigate = useNavigate();
  const [optinName, setOptinName] = useState('');
  const [optinCountry, setOptinCountry] = useState<CountryOption | null>(null);
  const [optinEmail, setOptinEmail] = useState('');
  const [optinError, setOptinError] = useState<string | null>(null);
  const [optinConsent, setOptinConsent] = useState(false);
  const [optinSubmitting, setOptinSubmitting] = useState(false);

  const handleOptinSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (optinSubmitting) {
      return;
    }

    const trimmedName = optinName.trim();
    const trimmedEmail = optinEmail.trim();

    if (!trimmedName) {
      setOptinError('Name is required.');
      return;
    }

    if (!optinCountry) {
      setOptinError('Please select your country.');
      return;
    }

    if (!trimmedEmail) {
      setOptinError('Email is required.');
      return;
    }

    if (!emailPattern.test(trimmedEmail)) {
      setOptinError('Please enter a valid email address.');
      return;
    }

    if (!optinConsent) {
      setOptinError('Please confirm consent before continuing.');
      return;
    }

    setOptinError(null);
    setOptinSubmitting(true);

    try {
      await submitWaitlist({
        email: trimmedEmail,
        name: trimmedName,
        country: optinCountry.value,
        source,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to join waitlist';
      setOptinError(message);
      setOptinSubmitting(false);
      return;
    }

    setOptinSubmitting(false);
    navigate(`/get?email=${encodeURIComponent(trimmedEmail)}`);
  };

  return (
    <section className={cn('py-24 sm:py-28', className)} id={id}>
      <div className="mx-auto w-full max-w-[760px] px-4 sm:px-6 lg:px-6">
        <div className="rounded-[28px] border-2 border-dashed border-black/25 bg-[#f7f7f7] px-3 py-10 sm:px-4 sm:py-12">
          <h2 className="mx-auto max-w-4xl text-center font-display text-5xl leading-[0.95] text-black sm:text-6xl lg:text-7xl">
            <span className="text-[#9ea2a8]">Get Access to</span>
            <br />
            <span className="text-black">Profit Cars.</span>
          </h2>

          <form className="mx-auto mt-10 w-full max-w-[560px] px-2 sm:px-0" onSubmit={handleOptinSubmit}>
            <div className="mx-auto w-full max-w-[312px] sm:max-w-[390px] lg:max-w-[520px]">
              <label className="text-sm font-semibold text-black" htmlFor={id ? `${id}-name` : 'optin-name'}>
                Name
              </label>
              <div className="mt-2 flex h-[66px] w-full items-center gap-3 rounded-[16px] border border-black/15 bg-white px-4 sm:px-5">
                <User className="h-6 w-6 shrink-0 text-black/65 sm:h-6 sm:w-6" strokeWidth={1.85} />
                <input
                  className="h-full w-full bg-transparent text-base font-semibold text-black outline-none placeholder:text-slate-400 sm:text-lg"
                  disabled={optinSubmitting}
                  id={id ? `${id}-name` : 'optin-name'}
                  onChange={(event) => {
                    setOptinName(event.target.value);
                    if (optinError) {
                      setOptinError(null);
                    }
                  }}
                  placeholder="Enter your name"
                  type="text"
                  value={optinName}
                />
              </div>
            </div>

            <div className="mx-auto mt-4 w-full max-w-[312px] sm:max-w-[390px] lg:max-w-[520px]">
              <label className="text-sm font-semibold text-black" htmlFor={id ? `${id}-country` : 'optin-country'}>
                Country
              </label>
              <div className="mt-2 flex h-[66px] w-full items-center gap-3 rounded-[16px] border border-black/15 bg-white px-4 sm:px-5">
                <MapPin className="h-6 w-6 shrink-0 text-black/65 sm:h-6 sm:w-6" strokeWidth={1.85} />
                <Select<CountryOption, false>
                  className="w-full"
                  formatOptionLabel={formatCountryOption}
                  inputId={id ? `${id}-country` : 'optin-country'}
                  isClearable
                  isDisabled={optinSubmitting}
                  isSearchable
                  noOptionsMessage={() => 'No matching country'}
                  onChange={(selectedOption: SingleValue<CountryOption>) => {
                    setOptinCountry(selectedOption);
                    if (optinError) {
                      setOptinError(null);
                    }
                  }}
                  options={europeanCountryOptions}
                  placeholder="Select your country"
                  styles={countrySelectStyles}
                  value={optinCountry}
                />
              </div>
            </div>

            <div className="mx-auto mt-4 w-full max-w-[312px] sm:max-w-[390px] lg:max-w-[520px]">
              <label className="text-sm font-semibold text-black" htmlFor={id ? `${id}-email` : 'optin-email'}>
                Business email
              </label>
              <div className="mt-2 flex h-[66px] w-full items-center gap-3 rounded-[16px] border border-black/15 bg-white px-4 sm:px-5">
                <Mail className="h-6 w-6 shrink-0 text-black/65 sm:h-6 sm:w-6" strokeWidth={1.85} />
                <input
                  className="h-full w-full bg-transparent text-base font-semibold text-black outline-none placeholder:text-slate-400 sm:text-lg"
                  disabled={optinSubmitting}
                  id={id ? `${id}-email` : 'optin-email'}
                  onChange={(event) => {
                    setOptinEmail(event.target.value);
                    if (optinError) {
                      setOptinError(null);
                    }
                  }}
                  placeholder="Enter your email"
                  type="email"
                  value={optinEmail}
                />
              </div>
            </div>

            <label className="mx-auto mt-6 flex w-full max-w-[312px] items-start gap-2.5 text-left text-sm leading-relaxed text-slate-500 sm:max-w-[390px] lg:max-w-[520px]">
              <input
                checked={optinConsent}
                className="mt-1 h-4 w-4 rounded border border-black/35 accent-[#48a7e6]"
                disabled={optinSubmitting}
                onChange={(event) => {
                  setOptinConsent(event.target.checked);
                  if (optinError) {
                    setOptinError(null);
                  }
                }}
                type="checkbox"
              />
              <span>
                I agree to receiving email updates from Profit Cars and confirm that I read and agree with{' '}
                <span className="underline">Terms of Use</span> and <span className="underline">Privacy Policy</span>.
              </span>
            </label>

            {optinError && (
              <p
                className="mx-auto mt-4 w-full max-w-[312px] rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 sm:max-w-[390px] lg:max-w-[520px]"
                role="alert"
              >
                {optinError}
              </p>
            )}

            <div className="mt-9 flex justify-center">
              <Button
                className="relative isolate flex h-14 w-full max-w-[312px] overflow-hidden rounded-[10px] border border-black/65 bg-[#08090c] px-7 text-lg font-bold tracking-[0.04em] text-white shadow-none hover:border-white/35 hover:bg-[#141820] focus-visible:outline-[#08090c] after:pointer-events-none after:absolute after:inset-y-0 after:-left-[55%] after:w-[42%] after:skew-x-[-22deg] after:bg-gradient-to-r after:from-transparent after:via-white/55 after:to-transparent after:opacity-0 after:transition-all after:duration-700 hover:after:left-[130%] hover:after:opacity-100 sm:max-w-[390px] lg:max-w-[520px]"
                disabled={optinSubmitting}
                type="submit"
              >
                {optinSubmitting ? 'Submitting...' : 'Get access'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
